import {
  AutocompleteRenderOptionState,
  Checkbox,
  Chip,
  ListItemButton,
} from "@mui/material";
import { HTMLAttributes, ReactElement } from "react";

import { ListTenantsWithSubaccountsQuery } from "~frontend/__generated__/graphql";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";

import { SearchableSelect, SearchableSelectProps } from "./FormSelect";

type SearchableSelectAnyProps = SearchableSelectProps<string>;

interface TenantDropdownSingleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  value?: string;
  onChange?: (tenantId?: string) => void | Promise<void>;
  multiple?: false;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface TenantDropdownMultipleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  value?: string[];
  onChange?: (tenantIds: string[]) => void | Promise<void>;
  multiple: true;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export type TenantDropdownProps =
  | TenantDropdownSingleProps
  | TenantDropdownMultipleProps;

export function TenantDropdown(props: TenantDropdownProps): ReactElement {
  const { tenants, label = "STW-T Tenant", placeholder, disabled } = props;

  const tenantOptions = tenants.map(tenant => ({
    label: `${tenant.name} (${tenant.slug})`,
    id: tenant.id,
  }));

  const searchableSelectProps = {
    options: tenantOptions,
    label,
    placeholder,
    disabled,
    value: props.value,
    onChange: props.onChange,
    ...(props.multiple && {
      multiple: true as const,
      renderOption: (
        itemProps: HTMLAttributes<HTMLLIElement> & { key: any },
        option: { label: string; id: string },
        { selected }: AutocompleteRenderOptionState
      ) => {
        const { key, ...otherProps } = itemProps;
        return (
          <ListItemButton component="li" key={key} {...otherProps}>
            <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
            {option.label}
          </ListItemButton>
        );
      },
    }),
  } as SearchableSelectAnyProps;

  return <SearchableSelect<string> {...searchableSelectProps} />;
}

export type SubaccountDropdownItem = {
  id: string;
  name: string;
  slug: string;
  isWeText?: boolean;
};

interface SubaccountDropdownCommonProps {
  tenants?: ListTenantsWithSubaccountsQuery["listTenants"];
  selectedTenantId?: string;
  subaccounts?: SubaccountDropdownItem[];
  disabledSubaccountIds?: string[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface SubaccountDropdownSingleProps extends SubaccountDropdownCommonProps {
  value?: string;
  onChange?: (subaccountId?: string) => void | Promise<void>;
  multiple?: false;
}

interface SubaccountDropdownMultipleProps extends SubaccountDropdownCommonProps {
  value?: string[];
  onChange?: (subaccountIds: string[]) => void | Promise<void>;
  multiple: true;
}

export type SubaccountDropdownProps =
  | SubaccountDropdownSingleProps
  | SubaccountDropdownMultipleProps;

type SubaccountOption = { label: string; id: string; isWeText: boolean };

export function SubaccountDropdown(
  props: SubaccountDropdownProps
): ReactElement {
  const {
    tenants,
    selectedTenantId,
    subaccounts,
    disabledSubaccountIds,
    label = "STW-T Subaccount",
    placeholder,
    disabled,
  } = props;

  const items: SubaccountDropdownItem[] =
    subaccounts ??
    (tenants?.find(t => t.id === selectedTenantId)?.subaccounts ?? []).map(
      sa => ({ id: sa.id, name: sa.name, slug: sa.slug })
    );

  const subaccountOptions: SubaccountOption[] = items.map(subaccount => ({
    label: `${subaccount.name} (${subaccount.slug})`,
    id: subaccount.id,
    isWeText: subaccount.isWeText ?? false,
  }));

  const hasAnyWeTextMetadata = items.some(item => item.isWeText !== undefined);

  const disabledIds = new Set(disabledSubaccountIds ?? []);
  const getOptionDisabled = disabledSubaccountIds
    ? (option: SubaccountOption) => disabledIds.has(option.id)
    : undefined;

  const renderMultipleOption = (
    itemProps: HTMLAttributes<HTMLLIElement> & { key: any },
    option: SubaccountOption,
    { selected }: AutocompleteRenderOptionState
  ) => {
    const { key, ...otherProps } = itemProps;
    return (
      <ListItemButton component="li" key={key} {...otherProps}>
        <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
        {option.label}
        {option.isWeText && (
          <Chip
            label="WeText"
            size="small"
            color="primary"
            sx={{ ml: 1, height: 18, fontSize: "10px" }}
          />
        )}
      </ListItemButton>
    );
  };

  const renderSingleOption = (
    itemProps: HTMLAttributes<HTMLLIElement> & { key: any },
    option: SubaccountOption
  ) => {
    const { key, ...otherProps } = itemProps;
    return (
      <ListItemButton component="li" key={key} {...otherProps}>
        {option.label}
        {option.isWeText && (
          <Chip
            label="WeText"
            size="small"
            color="primary"
            sx={{ ml: 1, height: 18, fontSize: "10px" }}
          />
        )}
      </ListItemButton>
    );
  };

  const searchableSelectProps = {
    options: subaccountOptions,
    label,
    placeholder,
    disabled,
    value: props.value,
    onChange: props.onChange,
    getOptionDisabled,
    ...(props.multiple
      ? {
          multiple: true as const,
          renderOption: renderMultipleOption,
        }
      : hasAnyWeTextMetadata
        ? { renderOption: renderSingleOption }
        : {}),
  } as SearchableSelectAnyProps;

  return <SearchableSelect<string> {...searchableSelectProps} />;
}
